import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { AutoCompleteDropdownComponent } from '../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';
import { SwitchControlComponent } from '../../shared/controls/switch-control/switch-control.component';

import { ErrorService } from '../../shared/services/error-service';
import { ToastService } from '../../shared/toast/toast.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LoadingHelper } from '../../shared/services/loading-helper';

import { ExchangeRateService, CurrencyService, AllCurrencyItem, CreateExchangeRateInput, UpdateExchangeRateInput } from '../../../service/proxy';

@Component({
    templateUrl: './create-or-update-exchange-rate.component.html',
    styleUrls: ['./create-or-update-exchange-rate.component.scss'],
    standalone: true,

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent,
        SwitchControlComponent
    ],

    providers: [
        ExchangeRateService,
        CurrencyService
    ],
})
export class CreateOrUpdateExchangeRateComponent implements OnInit {
    public exchangeRateId: number = 0;
    public exchangeRate: any = {};
    public currencies: AllCurrencyItem[] = [];

    public addAnother: boolean = false;
    public formSubmitted: boolean = false;

    // hold incoming date query param until after load
    private pendingQueryDate: string | null = null;
    private pendingQueryCurrencyId: number | null = null;
    private pendingQueryValue: number | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private exchangeRateService: ExchangeRateService,
        private currencyService: CurrencyService,
        private errorService: ErrorService,
        private toastService: ToastService,
        private loadingService: LoadingService
    ) {}

    ngOnInit(): void {
        this.exchangeRateId = Number(this.route.snapshot.paramMap.get('id'));

        // read date query param if present
        const dateParam = this.route.snapshot.queryParamMap.get('date');

        if (dateParam) {
            this.pendingQueryDate = dateParam;
        }

        const currencyIdParam = this.route.snapshot.queryParamMap.get('currencyId');

        if (currencyIdParam !== null) {
            const parsedCurrencyId = Number(currencyIdParam);

            if (!isNaN(parsedCurrencyId)) {
                this.pendingQueryCurrencyId = parsedCurrencyId;
            }
        }

        const valueParam = this.route.snapshot.queryParamMap.get('value');

        if (valueParam !== null) {
            const parsedValue = Number(valueParam);

            if (!isNaN(parsedValue)) {
                this.pendingQueryValue = parsedValue;
            }
        }

        this.getCurrencies();

        if (this.exchangeRateId > 0) {
            this.getExchangeRate(this.exchangeRateId);
        }
        else if (this.pendingQueryDate) {
            // creating new - apply date from query
            this.exchangeRate.date = this.pendingQueryDate;
            this.pendingQueryDate = null;

            if (this.pendingQueryCurrencyId !== null) {
                this.exchangeRate.currencyId = this.pendingQueryCurrencyId;
                this.pendingQueryCurrencyId = null;
            }

            if (this.pendingQueryValue !== null) {
                this.exchangeRate.value = this.pendingQueryValue;
                this.pendingQueryValue = null;
            }
        }
        else {
            if (this.pendingQueryCurrencyId !== null) {
                this.exchangeRate.currencyId = this.pendingQueryCurrencyId;
                this.pendingQueryCurrencyId = null;
            }

            if (this.pendingQueryValue !== null) {
                this.exchangeRate.value = this.pendingQueryValue;
                this.pendingQueryValue = null;
            }
        }
    }

    getExchangeRate(exchangeRateId: number): void {
        if (exchangeRateId > 0) {
            this.exchangeRateService.getExchangeRateById(exchangeRateId)
                .subscribe((data) => {

                    if (data.date) {
                        this.exchangeRate = {
                            ...data,
                            date: (typeof data.date === 'string' ? data.date : data.date.toISOString()).split('T')[0]
                        };
                    }
                    else {
                        this.exchangeRate = data;
                    }

                    if (this.pendingQueryDate) {
                        this.exchangeRate.date = this.pendingQueryDate;
                        this.pendingQueryDate = null;
                    }
                }
            );
        }
    }

    getCurrencies(): void {
        this.currencyService.getAllCurrencies()
            .subscribe((data) => {
                this.currencies = data.results || [];
            }
        );
    }

    onCurrencySelectionChange(selectedId: number | null): void {
        this.exchangeRate.currencyId = selectedId ?? undefined;
    }

    displayCurrency(option: any): string {
        return option?.name && option?.symbol ? `${option.name} - ${option.symbol}` : '';
    }

    onSave(form: any): void {
        this.formSubmitted = true;

        if (!form.valid || !this.exchangeRate.currencyId) {
            return;
        }

        if (this.exchangeRateId <= 0) {
            this.createExchangeRate();
        }
        else {
            this.updateExchangeRate();
        }
    }

    onCancel(): void {
        if (this.exchangeRate && this.exchangeRate.date) {
            this.router.navigate([`/exchange-rate`], { queryParams: { date: this.exchangeRate.date } });
        }
        else {
            this.router.navigate([`/exchange-rate`]);
        }
    }

    createExchangeRate(): void {
        const input = this.exchangeRate as CreateExchangeRateInput;

        LoadingHelper.withLoading(
            this.exchangeRateService.createExchangeRate(input),
            this.loadingService
        ).subscribe({
            next: () => {
                this.toastService.show('Exchange rate created successfully', 'success');
                this.addAnotherOrRedirect();
            },
            error: (error) => {
                this.toastService.show('Failed to create Exchange rate', 'error');
                this.errorService.handleError(error);
            },
        });
    }

    updateExchangeRate(): void {
        const input = this.exchangeRate as UpdateExchangeRateInput;

        LoadingHelper.withLoading(
            this.exchangeRateService.updateExchangeRate(this.exchangeRateId, input),
            this.loadingService
        ).subscribe({
            next: () => {
                this.toastService.show('Exchange rate updated successfully', 'success');
                this.addAnotherOrRedirect();
            },
            error: (error) => {
                this.toastService.show('Failed to update Exchange rate', 'error');
                this.errorService.handleError(error);
            },
        });
    }

    addAnotherOrRedirect(): void {
        if (this.addAnother) {
            this.exchangeRate = {
                date: this.exchangeRate.date
            };
        }
        else {
            if (this.exchangeRate && this.exchangeRate.date) {
                this.router.navigate(['/exchange-rate'], { queryParams: { date: this.exchangeRate.date } });
            }
            else {
                this.router.navigate(['/exchange-rate']);
            }
        }
    }
}